import { useToasterStore } from "@/stores/toaster";
import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import { expandThumb } from "@paparazzi/components/stores/thumbnail_conversion";
import jszip from "jszip";
import type { Ref } from "vue";

export interface DownloadLink {
  filename: string;
  url: string;
}

async function UrlToDataUrl(url: string): Promise<string> {
  const response = await fetch(url);
  const blob = await response.blob();
  return blobToDataUrl(blob);
}

export function blobToDataUrl(blob: Blob): Promise<string> {
  const reader = new FileReader();
  return new Promise((resolve, reject) => {
    reader.onload = (e) => resolve(e.target?.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

async function loadCapacitorMedia() {
  let module: any = { Media: null };
  try {
    module = await import("@capacitor-community/media");
    await module.Media.savePhoto();
  } catch (e) {
    if (/not implemented/.test(`${e}`)) {
      console.error("error loading media module", e);
      module = null;
    }
  }
  return module;
}

export async function downloadToGallery(
  uris: DownloadLink[],
  albumName = "PaparazziImages",
  update?: Ref<number>,
) {
  const toaster = useToasterStore();
  const { Media } = await loadCapacitorMedia();

  let album = { identifier: undefined };
  if (Capacitor.getPlatform() !== "ios") {
    let { albums } = await Media.getAlbums();
    album = albums.find((a: any) => a.name === albumName);
    if (!album) {
      album = await Media.createAlbum({ name: albumName });
      albums = (await Media.getAlbums()).albums;
      album = albums.find((a: any) => a.name === albumName);
    }
  }

  let i = 0;
  for (const uri of uris) {
    const saved = await Media.savePhoto({
      path: expandThumb(uri.url),
      albumIdentifier: album.identifier,
      filename: uri.filename,
    });
    i += 1;
    if (update) {
      update.value = i / uris.length;
    }
  }
  toaster.add({
    severity: "success",
    detail: "Images have been downloaded to your device",
  });
}

export async function download(
  uris: DownloadLink[],
  description = "",
  folder = "PaparazziImages",
  update?: Ref<number>,
) {
  const toaster = useToasterStore();
  const extendedUris = uris.map((u) => {
    return {
      filename: u.filename,
      url: expandThumb(u.url),
    };
  });

  if (Capacitor.getPlatform() !== "web") {
    try {
      const Media = await loadCapacitorMedia();
      if (Media) {
        const retval = await downloadToGallery(uris, folder, update);
        return retval;
      } else {
        return share(uris, description);
      }
    } catch (ex) {
      if (ex instanceof Error) {
        console.error(ex);
        toaster.add({
          severity: "error",
          detail: `Download failed, please try again (${ex.message})`,
        });
      }
    }
  } else if (document.createElement("a")) {
    let count = 0;
    for (const uri of extendedUris) {
      let link = document.createElement("a");
      link.download = uri.filename;
      link.style.display = "none";

      // this is required to make the "download" attribute work
      // it won't work with a foreign domain, even if it doesn't work on localhost
      link.href = uri.url.replace(
        /https?:\/\/vw-paparazzi.storage.googleapis.com\//,
        "/static/bucket/",
      );

      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      await new Promise((r) => setTimeout(r, 500));
      count += 1;
      if (update) {
        update.value = count / uris.length;
      }
    }
  } else {
    let uri = extendedUris[0].url;
    window.open(uri, "_system");
  }
}

export async function share(uris: DownloadLink[], description?: string) {
  let files = uris.map((u) => expandThumb(u.url));

  if (Capacitor.getPlatform() !== "web") {
    const promises: Promise<{ uri: string }>[] = [];
    uris.forEach((url) => {
      promises.push(
        (async () => {
          const body = await UrlToDataUrl(expandThumb(url.url));
          const entry = await Filesystem.writeFile({
            path: `Images/${url.filename}`,
            data: body,
            directory: Directory.Cache,
            recursive: true,
          });
          return entry;
        })(),
      );
    });
    files = (await Promise.all(promises)).map((e) => e.uri);
  }

  try {
    return Share.share({
      text: description,
      files,
    });
  } catch (ex) {
    console.log("failed to share", ex);
  }
}

export async function zip(
  uris: DownloadLink[],
  opts: {
    filename?: string;
    update?: Ref<number>;
  } = {
    filename: "download.zip",
  },
): Promise<DownloadLink> {
  let zip = new jszip();
  let count = 0;
  const promises = [];
  for (let uri of uris) {
    const promise = fetch(expandThumb(uri.url));
    promise.then((b) => {
      console.log("saving...", uri.filename);
      zip.file(uri.filename, b.blob(), { binary: true });
      count += 1;
      if (opts.update) {
        opts.update.value = count / uris.length;
      }
    });
    promise.catch((ex) => {
      console.error("error", ex);
      count += 1;
      if (opts.update) {
        opts.update.value = count / uris.length;
      }
    });
    promises.push(promise);
  }
  await Promise.all(promises);
  return {
    filename: opts.filename ?? "",
    url: await blobToDataUrl(await zip.generateAsync({ type: "blob" })),
  };
}
